import { createFeatureSelector, createSelector } from '@ngrx/store';
import { INVOICE_MAX_SELECTION_ALLOWED } from '../../../constants/default.const';
import { Invoice } from '../../../models/invoice-model';
import { InvoicesState, invoicesFeatureKey } from './invoices.reducers';

export const invoiceSelector = createFeatureSelector<InvoicesState>(invoicesFeatureKey);

export const getPageNumberInvoices = createSelector(invoiceSelector, (state: InvoicesState) => state.pageNumber);

export const getInvoices = createSelector(invoiceSelector, (state: InvoicesState) => state.invoices);

export const getNoMoreRecordInvoices = createSelector(invoiceSelector, (state: InvoicesState) => state.noMoreRecord);

export const getInvoiceFilters = createSelector(invoiceSelector, (state: InvoicesState) => state.filters);

export const getInvoiceTotalDue = createSelector(invoiceSelector, (state: InvoicesState) => state.invoiceTotalDue);

export const selectInvoiceDetail = createSelector(invoiceSelector, (state: InvoicesState) => {
    if (state.invoiceDetail) {
        return state.invoiceDetail;
    }
    return null;
});

export const getSelectedInvoices = createSelector(invoiceSelector, (state: InvoicesState) => state.selectedInvoices);

export const getSelectedInvoicesModels = createSelector(invoiceSelector, getSelectedInvoices, (state: InvoicesState, selectedInvoices: string[]) =>
    state.invoices.filter((invoice: Invoice) => selectedInvoices.includes(invoice.Id))
);

export const getInvoicesTotal = createSelector(invoiceSelector, getSelectedInvoices, (state: InvoicesState, selectedInvoices: string[]) => {
    let total = 0;

    state.invoices
        .filter((invoice: Invoice) => selectedInvoices.includes(invoice.Id))
        .forEach((invoice: Invoice) => {
            total += Number(invoice.AmountDue);
        });

    return Math.round((total + Number.EPSILON) * 100) / 100;
});

export const isInvoiceSelected = (invoiceId: string) =>
    createSelector(getSelectedInvoices, (selectedInvoices: string[]) => {
        return selectedInvoices.includes(invoiceId);
    });

/**
 * Leg-1 stub for payability. Only enforces the selection cap and a positive AmountDue.
 * Leg 4 (ADO #109710) replaces this with the paymentBoundaries answer.
 */
export const isInvoiceSelectable = (invoiceId: string) =>
    createSelector(invoiceSelector, getSelectedInvoices, isInvoiceSelected(invoiceId), (state: InvoicesState, selectedInvoices: string[], isSelected: boolean) => {
        const selectingInvoice = state.invoices.find((invoice: Invoice) => invoice.Id === invoiceId);

        // Allow already selected invoices to remain selectable
        if (isSelected) {
            return true;
        }
        if (selectedInvoices.length === INVOICE_MAX_SELECTION_ALLOWED) {
            return false;
        }
        if (Number(selectingInvoice?.AmountDue) <= 0) {
            return false;
        }

        return true;
    });

export const isMaxInvoiceSelected = createSelector(getSelectedInvoices, (selectedInvoices: string[]) => {
    return selectedInvoices.length === INVOICE_MAX_SELECTION_ALLOWED;
});

/**
 * ADO-116163: single source of truth for whether the current invoice selection is payable
 * (leg 3 = positive total only). Leg 4 (ADO-116164) refines this via payment boundaries —
 * change the rule HERE, one place. Do not scatter `AmountDue > 0` / `total > 0` checks in effects.
 */
export const areSelectedInvoicesPayable = createSelector(getInvoicesTotal, (total: number): boolean => total > 0);
